import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, PhoneCall, RefreshCw, Unlink, Save } from 'lucide-react';

// Admin card (Team page): org-level Webex Calling connection + line-to-user mapping.
export function WebexCard() {
  const [status, setStatus] = useState(null);
  const [lines, setLines] = useState([]);
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saToken, setSaToken] = useState('');
  const [saBusy, setSaBusy] = useState(false);

  const load = () => {
    api.webexStatus().then((r) => setStatus(r.data)).catch(() => {});
    api.webexLines().then((r) => setLines(r.data.lines || [])).catch(() => {});
    api.listTeam().then((r) => setTeam((r.data || []).filter(u => u.role !== 'ac'))).catch(() => {});
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const res = params.get('webex');
    if (res === 'connected') {
      toast.success('Webex Calling connected — call sync is running now');
      window.history.replaceState({}, '', '/team');
    } else if (res === 'error') {
      toast.error(`Webex connection failed (${params.get('reason') || 'unknown'})`);
      window.history.replaceState({}, '', '/team');
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.webexConnect();
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start Webex login');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.webexDisconnect();
      toast.success('Webex disconnected');
      load();
    } catch { toast.error('Could not disconnect'); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.webexSync();
      if (r.data.last_error) toast.error(`Sync ran with error: ${r.data.last_error}`);
      else toast.success('Call sync complete');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const connectServiceApp = async () => {
    setSaBusy(true);
    try {
      await api.webexServiceApp(saToken.trim());
      toast.success('Webex Service App connected — call sync is running');
      setSaToken('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Token was rejected');
    } finally { setSaBusy(false); }
  };

  const setLine = (i, field, value) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [field]: value } : l)));

  const saveLines = async () => {
    setSaving(true);
    try {
      const r = await api.webexUpdateLines(lines);
      setLines(r.data.lines || []);
      toast.success('Line mapping saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-sm bg-card p-4 space-y-3" data-testid="webex-card">
      <div className="flex items-center gap-2 flex-wrap">
        <PhoneCall className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
        <h3 className="text-sm font-medium text-foreground">Webex Calling</h3>
        {status?.connected ? (
          <span className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5"
            data-testid="webex-status-badge">connected · {status.account}</span>
        ) : (
          <span className="text-[10px] font-mono border border-border text-muted-foreground rounded px-1.5 py-0.5"
            data-testid="webex-status-badge">not connected</span>
        )}
        <span className="ml-auto flex gap-1.5">
          {status?.connected && (
            <Button size="sm" variant="outline" onClick={syncNow} disabled={syncing} data-testid="webex-sync-btn"
              className="h-7 text-xs rounded-sm border-border">
              {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync now
            </Button>
          )}
          {status?.connected ? (
            <Button size="sm" variant="outline" onClick={disconnect} disabled={busy} data-testid="webex-disconnect-btn"
              className="h-7 text-xs rounded-sm border-border text-red-400 hover:text-red-300">
              <Unlink className="w-3 h-3" /> Disconnect
            </Button>
          ) : (
            <Button size="sm" onClick={connect} disabled={busy || !status?.configured} data-testid="webex-connect-btn"
              className="h-7 text-xs rounded-sm">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PhoneCall className="w-3 h-3" />} Connect Webex
            </Button>
          )}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Pulls the detailed call history every 2 minutes (Webex provides records ~5 min after a call ends), matches
        callers against contact phone numbers and logs matched calls as Call + Engagement on the deal.
      </p>
      {!status?.configured && (
        <p className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2 py-1.5"
          data-testid="webex-not-configured">
          WEBEX_CLIENT_ID / WEBEX_CLIENT_SECRET are not configured yet — create the integration on developer.webex.com
          and add the credentials to the backend environment.
        </p>
      )}
      {status?.last_error && (
        <p className="text-[11px] text-red-400" data-testid="webex-last-error">Last error: {status.last_error}</p>
      )}
      {!status?.connected && status?.configured && (
        <div className="space-y-1.5 border border-border rounded-sm p-2.5 bg-secondary/30" data-testid="webex-serviceapp-box">
          <p className="text-xs font-medium text-foreground">Connect via Service App token (recommended)</p>
          <p className="text-[11px] text-muted-foreground">
            developer.webex.com → your app → <span className="text-foreground">Org Authorizations</span> → select your
            org → enter the client secret → copy the generated <span className="text-foreground">refresh token</span> and paste it here.
          </p>
          <div className="flex items-center gap-2">
            <Input value={saToken} onChange={e => setSaToken(e.target.value)} placeholder="Refresh token…"
              type="password" data-testid="webex-serviceapp-token-input"
              className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs flex-1" />
            <Button size="sm" onClick={connectServiceApp} disabled={saBusy || !saToken.trim()}
              data-testid="webex-serviceapp-save-btn" className="h-7 text-xs rounded-sm">
              {saBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PhoneCall className="w-3 h-3" />} Connect
            </Button>
          </div>
        </div>
      )}
      {status?.connected && (
        <p className="text-[11px] text-muted-foreground">
          Last sync: {status.last_sync_at ? new Date(status.last_sync_at).toLocaleString('de-DE') : '—'} ·
          {' '}{status.total_calls} calls tracked · {status.unmatched_calls} unmatched
        </p>
      )}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-foreground">Line mapping (number → team member)</p>
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`webex-line-${i}`}>
            <Input value={l.number} onChange={e => setLine(i, 'number', e.target.value)}
              data-testid={`webex-line-number-${i}`}
              className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs w-44" />
            <Input value={l.label} onChange={e => setLine(i, 'label', e.target.value)} placeholder="Label"
              data-testid={`webex-line-label-${i}`}
              className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs w-40" />
            <select value={l.user_id || ''} onChange={e => setLine(i, 'user_id', e.target.value)}
              data-testid={`webex-line-user-${i}`}
              className="text-xs bg-secondary border border-border text-foreground rounded-sm px-2 h-7 cursor-pointer focus:outline-none">
              <option value="">— no user (e.g. Zentrale) —</option>
              {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={saveLines} disabled={saving} data-testid="webex-save-lines-btn"
          className="h-7 text-xs rounded-sm border-border mt-1">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save mapping
        </Button>
      </div>
    </div>
  );
}
